/**
 * Primeira tela do aplicativo: a estimativa de biometria.
 *
 * Escolhida de propósito para ser a primeira. Ela não depende de banco, de
 * login nem de sincronização — então prova que o caminho inteiro funciona
 * (compilação, publicação, celular, offline) e já é útil no mesmo dia, na hora
 * de decidir se despesca.
 *
 * Repare em como os números atravessam a tela: o campo devolve texto, o texto
 * vira número cru, e o número cru só vira Gramas ou Quilos na fronteira, pelos
 * construtores de `unidades`. Daí para dentro o compilador não deixa mais
 * trocar uma unidade pela outra.
 */

import { useMemo, useState } from 'react'
import { RegistrarBiometria } from './RegistrarBiometria'
import type { Perfil } from '../acesso/api'
import {
  biomassa, cargaDoDia, densidade, estacaoDoMes, estimarFaixa, m2PorPeixe,
  metaDoDia, TETO_OBSERVADO,
} from '../dominio/calculos'
import {
  ca, emKg, fracao, g, ha, haParaM2, inteiro, kg, numero, numeroPtBr, peixes,
} from '../dominio/unidades'

/** As três conversões que o gestor considera plausíveis na beira do tanque. */
const CAS_PLAUSIVEIS = [ca(1.7), ca(2.0), ca(2.3)] as const

interface CampoProps {
  rotulo: string
  sufixo: string
  valor: string
  aoMudar: (v: string) => void
  dica?: string
}

function Campo({ rotulo, sufixo, valor, aoMudar, dica }: CampoProps) {
  return (
    <label className="campo">
      <span className="campo-rotulo">{rotulo}</span>
      <span className="campo-caixa">
        <input
          type="text"
          inputMode="decimal"
          value={valor}
          onChange={(e) => aoMudar(e.target.value)}
          aria-label={rotulo}
        />
        <span className="campo-sufixo">{sufixo}</span>
      </span>
      {dica ? <span className="campo-dica">{dica}</span> : null}
    </label>
  )
}

/** Vírgula é decimal, ponto é milhar — o padrão do sistema. */
function paraNumero(texto: string): number {
  return numeroPtBr(texto) ?? 0
}

export function Biometria({ perfil }: { perfil: Perfil }) {
  // A tela tem duas metades, e a ordem importa. Em cima o REGISTRO: a balança
  // entrando no banco, que é o que ancora todo o resto do app. Embaixo a
  // CALCULADORA, que estima quando não há balança — útil na beira do tanque
  // para decidir se despesca, e que não grava nada.
  const [refeita, setRefeita] = useState(0)
  const [qtdTexto, setQtd] = useState('4300')
  const [pesoEntradaTexto, setPesoEntrada] = useState('280')
  const [racaoTexto, setRacao] = useState('18000')
  const [areaTexto, setArea] = useState('3,5')

  const conta = useMemo(() => {
    const quantidade = peixes(paraNumero(qtdTexto))
    const pesoEntrada = g(paraNumero(pesoEntradaTexto))
    const racao = kg(paraNumero(racaoTexto))
    const area = haParaM2(ha(paraNumero(areaTexto)))

    const faixa = estimarFaixa(racao, CAS_PLAUSIVEIS, quantidade, pesoEntrada)
    const central = faixa[1] ?? faixa[0]
    const pesoAtual = central?.pesoMedioEstimado ?? pesoEntrada

    const bio = biomassa(quantidade, pesoAtual)
    const dens = densidade(bio, area)
    const espaco = m2PorPeixe(area, quantidade)

    // Quem manda hoje: o apetite do peixe ou a capacidade da água.
    // 1,5% do peso vivo é a faixa de 1.200–1.800 g na tabela da fazenda.
    const estacao = estacaoDoMes(new Date().getMonth() + 1)
    const teto = TETO_OBSERVADO[estacao]
    const meta = metaDoDia(bio, fracao(0.015), teto, area)
    const carga = cargaDoDia(meta.meta, area)

    return { faixa, bio, dens, espaco, meta, estacao, teto, carga, quantidade }
  }, [qtdTexto, pesoEntradaTexto, racaoTexto, areaTexto])

  const { faixa, bio, dens, espaco, meta, estacao, teto, carga } = conta

  return (
    <div className="pagina">
      <RegistrarBiometria key={refeita} aoGravar={() => setRefeita((r) => r + 1)} />

      {/* A CALCULADORA É DO GESTOR, e some para o tratador.
          Ela estima o peso por outro caminho — ração acumulada dividida por
          uma conversão alimentar que a pessoa informa —, que não é o caminho
          que o app usa para sugerir o trato. Duas estimativas diferentes na
          mesma tela, para quem está no sol decidindo quanto jogar, não é
          informação a mais: é dúvida sobre em qual acreditar. */}
      {perfil === 'tratador' ? null : (
      <>
      <header className="topo">
        <h1>Estimar sem balança</h1>
        <p className="subtitulo">
          Quando não houve pesagem, quanto o peixe DEVE estar pesando — a partir
          da ração que já entrou no tanque e de uma conversão alimentar que você
          informa. Esta parte não grava nada: é conta de beira de tanque, para
          decidir se vale despescar.
        </p>
      </header>

      <section className="bloco">
        <h2>O ciclo</h2>
        <div className="campos">
          <Campo rotulo="Peixes no tanque" sufixo="peixes" valor={qtdTexto} aoMudar={setQtd} />
          <Campo
            rotulo="Peso de entrada"
            sufixo="g"
            valor={pesoEntradaTexto}
            aoMudar={setPesoEntrada}
            dica="o peso com que entrou NESTE tanque, não o do alevino"
          />
          <Campo rotulo="Ração consumida" sufixo="kg" valor={racaoTexto} aoMudar={setRacao} />
          <Campo rotulo="Área do tanque" sufixo="ha" valor={areaTexto} aoMudar={setArea} />
        </div>
      </section>

      <section className="bloco">
        <h2>Peso estimado</h2>
        <p className="aviso">
          É estimativa, não pesagem. Três conversões plausíveis, três respostas —
          a distância entre elas é a incerteza real.
        </p>
        <div className="faixa">
          {faixa.map((e, i) => (
            <div key={e.caInformada} className={`cartao ${i === 1 ? 'central' : ''}`}>
              <div className="cartao-rotulo">CA {numero(e.caInformada, 1)}</div>
              <div className="cartao-numero">{numero(e.pesoMedioEstimado / 1000, 2)}</div>
              <div className="cartao-unidade">kg por peixe</div>
              <div className="cartao-pe">ganho de {emKg(e.ganhoDeBiomassa)}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="bloco">
        <h2>O tanque hoje</h2>
        <dl className="linhas">
          <div><dt>Biomassa estimada</dt><dd>{inteiro(bio)} kg</dd></div>
          <div><dt>Densidade</dt><dd>{numero(dens, 2)} t/ha</dd></div>
          <div><dt>Espaço por peixe</dt><dd>{numero(espaco, 2)} m²</dd></div>
        </dl>
      </section>

      <section className="bloco">
        <h2>Quem manda na ração hoje</h2>
        <p className="aviso">
          Estação: <strong>{estacao}</strong> · teto observado de {inteiro(teto)} kg/ha/dia
        </p>
        <dl className="linhas">
          <div><dt>O peixe pede</dt><dd>{inteiro(meta.demandaBiologica)} kg/dia</dd></div>
          <div><dt>A água aceita</dt><dd>{inteiro(meta.tetoAmbiental)} kg/dia</dd></div>
          <div className={meta.quemManda === 'agua' ? 'destaque' : ''}>
            <dt>Meta do dia</dt>
            <dd>
              {inteiro(meta.meta)} kg/dia
              <span className="tag">{meta.quemManda === 'agua' ? 'a água manda' : 'o peixe manda'}</span>
            </dd>
          </div>
          <div><dt>Carga na água</dt><dd>{numero(carga, 1)} kg/ha/dia</dd></div>
        </dl>
      </section>

      <footer className="rodape">
        <p>
          As contas vêm de <code>src/dominio/calculos.ts</code>, o módulo único
          importado por todas as telas. A dedução de cada fórmula está em
          <code>docs/REGRAS-DE-CALCULO.md</code>.
        </p>
      </footer>
      </>
      )}
    </div>
  )
}
